import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Anchor, Menu, X, Phone, Mail, MessageCircle, ChevronDown } from "lucide-react";
import { useState } from "react";
import PageMeta from "@/components/PageMeta";
import CookieBanner from "@/components/CookieBanner";
import { COMPANY as C } from "@/lib/company";

export default function Layout() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const WHY_ITEMS = [
    { hash: "por-que", ...t.nav.whyItems.why },
    { hash: "propietarios", ...t.nav.whyItems.owners },
    { hash: "quienes-somos", ...t.nav.whyItems.about },
    { hash: "faq", ...t.nav.whyItems.faq },
  ];

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkCls = ({ isActive }) =>
    `font-display text-sm tracking-wide transition-colors ${isActive ? "text-[#0B192C]" : "text-slate-500 hover:text-[#0B192C]"}`;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageMeta />
      <CookieBanner />
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-link">
            <span className="font-display text-2xl font-semibold tracking-[0.14em] uppercase text-black">BEVELA</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {user ? (
              <>
                <NavLink to="/dashboard" className="nav-apple" data-testid="nav-home">{t.nav.home}</NavLink>
                <NavLink to="/explorar" className="nav-apple" data-testid="nav-explore">{t.nav.explore}</NavLink>
                <NavLink to="/mis-embarcaciones" className="nav-apple" data-testid="nav-my-boats">{t.nav.myBoats}</NavLink>
                {(user.intents?.includes("owner") || user.role === "admin") && <NavLink to="/gestion" className="nav-apple" data-testid="nav-manage">{t.nav.manage}</NavLink>}
                <NavLink to="/perfil" className="nav-apple" data-testid="nav-profile">{t.nav.profile}</NavLink>
                {user.role === "admin" && <NavLink to="/admin" className="nav-apple" data-testid="nav-admin">Admin</NavLink>}
              </>
            ) : (
              <>
                <NavLink to="/" end className="nav-apple" data-testid="nav-home-public">{t.nav.home}</NavLink>
                <NavLink to="/explorar" className="nav-apple" data-testid="nav-explore-public">{t.nav.explore}</NavLink>
                <div className="relative group">
                  <NavLink to="/por-que-bevela" className="nav-apple inline-flex items-center gap-1" data-testid="nav-why">
                    {t.nav.why} <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </NavLink>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition-all duration-200 z-50">
                    <div className="w-72 rounded-2xl bg-white shadow-xl border border-slate-100 p-2" data-testid="nav-why-menu">
                      {WHY_ITEMS.map((it) => (
                        <Link key={it.hash} to={`/por-que-bevela#${it.hash}`} className="block rounded-xl px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`nav-why-${it.hash}`}>
                          <p className="font-display text-sm text-[#0B192C]">{it.label}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{it.desc}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
                <Link to="/acceso" className="btn-nav-primary" data-testid="nav-access">{t.nav.access}</Link>
              </>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3 ml-6">
            <div className="flex items-center gap-1 text-xs font-display" data-testid="lang-switch">
              <button
                onClick={() => setLang("es")}
                className={`px-2 py-1 rounded-full transition-colors ${lang === "es" ? "bg-[#0B192C] text-white" : "text-slate-500 hover:text-[#0B192C]"}`}
                data-testid="lang-es"
              >ES</button>
              <button
                onClick={() => setLang("en")}
                className={`px-2 py-1 rounded-full transition-colors ${lang === "en" ? "bg-[#0B192C] text-white" : "text-slate-500 hover:text-[#0B192C]"}`}
                data-testid="lang-en"
              >EN</button>
            </div>
            {user && (
              <button onClick={onLogout} className="btn-ghost text-sm" data-testid="nav-logout">
                {t.nav.logout}
              </button>
            )}
          </div>

          <button className="md:hidden p-2 -mr-2" onClick={() => setMenuOpen((v) => !v)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white px-6 py-4 flex flex-col gap-3">
            {user ? (
              <>
                <NavLink to="/dashboard" onClick={() => setMenuOpen(false)} className="font-display text-slate-700">{t.nav.home}</NavLink>
                <NavLink to="/explorar" onClick={() => setMenuOpen(false)} className="font-display text-slate-700">{t.nav.explore}</NavLink>
                <NavLink to="/mis-embarcaciones" onClick={() => setMenuOpen(false)} className="font-display text-slate-700">{t.nav.myBoats}</NavLink>
                {(user.intents?.includes("owner") || user.role === "admin") && <NavLink to="/gestion" onClick={() => setMenuOpen(false)} className="font-display text-slate-700" data-testid="nav-manage-mobile">{t.nav.manage}</NavLink>}
                <NavLink to="/perfil" onClick={() => setMenuOpen(false)} className="font-display text-slate-700">{t.nav.profile}</NavLink>
                {user.role === "admin" && <NavLink to="/admin" onClick={() => setMenuOpen(false)} className="font-display text-slate-700">Admin</NavLink>}
                <button onClick={() => { setMenuOpen(false); onLogout(); }} className="text-left font-display text-slate-700">{t.nav.logout}</button>
              </>
            ) : (
              <>
                <Link to="/" onClick={() => setMenuOpen(false)} className="font-display text-slate-700">{t.nav.home}</Link>
                <Link to="/explorar" onClick={() => setMenuOpen(false)} className="font-display text-slate-700">{t.nav.explore}</Link>
                <button onClick={() => setWhyOpen((v) => !v)} className="flex items-center justify-between font-display text-slate-700" data-testid="nav-why-mobile">
                  {t.nav.why} <ChevronDown className={`h-4 w-4 transition-transform ${whyOpen ? "rotate-180" : ""}`} />
                </button>
                {whyOpen && (
                  <div className="pl-3 flex flex-col gap-2.5 border-l border-slate-100">
                    {WHY_ITEMS.map((it) => (
                      <Link key={it.hash} to={`/por-que-bevela#${it.hash}`} onClick={() => setMenuOpen(false)} className="text-sm text-slate-600" data-testid={`nav-why-mobile-${it.hash}`}>{it.label}</Link>
                    ))}
                  </div>
                )}
                <Link to="/acceso" onClick={() => setMenuOpen(false)} className="font-display text-slate-700" data-testid="nav-access-mobile">{t.nav.access}</Link>
              </>
            )}
            <div className="flex items-center gap-2 pt-2">
              <button onClick={() => setLang("es")} className={`px-3 py-1 rounded-full text-xs ${lang === "es" ? "bg-[#0B192C] text-white" : "border border-slate-200"}`}>ES</button>
              <button onClick={() => setLang("en")} className={`px-3 py-1 rounded-full text-xs ${lang === "en" ? "bg-[#0B192C] text-white" : "border border-slate-200"}`}>EN</button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-slate-100 pt-14 pb-8 mt-16 bg-[#F8F9FA]" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-2">
              <Anchor className="h-4 w-4 text-[#0B192C]" strokeWidth={1.6} />
              <span className="font-serif text-xl text-[#0B192C]">Bevela</span>
            </div>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xs">{t.tagline}</p>
            <div className="mt-6 text-xs text-slate-400 font-display leading-relaxed" data-testid="footer-company-data">
              <p>{C.legalName} · CIF {C.cif}</p>
              <p>{C.address}</p>
            </div>
          </div>
          <div className="md:col-span-2">
            <p className="font-display text-[0.68rem] tracking-[0.22em] uppercase text-slate-400">{t.footer.company}</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/por-que-bevela#quienes-somos" className="text-slate-600 hover:text-[#0B192C]" data-testid="footer-about">{t.footer.about}</Link></li>
              <li><Link to="/por-que-bevela#faq" className="text-slate-600 hover:text-[#0B192C]" data-testid="footer-faq">{t.footer.faq}</Link></li>
              <li><Link to="/explorar" className="text-slate-600 hover:text-[#0B192C]" data-testid="footer-explore">{t.footer.explore}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="font-display text-[0.68rem] tracking-[0.22em] uppercase text-slate-400">{t.footer.legal}</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/aviso-legal" className="text-slate-600 hover:text-[#0B192C]" data-testid="footer-legal-notice">{t.footer.legalNotice}</Link></li>
              <li><Link to="/privacidad" className="text-slate-600 hover:text-[#0B192C]" data-testid="footer-privacy">{t.footer.privacy}</Link></li>
              <li><Link to="/cookies" className="text-slate-600 hover:text-[#0B192C]" data-testid="footer-cookies">{t.footer.cookies}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="font-display text-[0.68rem] tracking-[0.22em] uppercase text-slate-400">{t.footer.contact}</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><a href={`tel:${C.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-slate-600 hover:text-[#0B192C]" data-testid="footer-phone"><Phone className="h-3.5 w-3.5" />{C.phone}</a></li>
              <li><a href={`mailto:${C.email}`} className="flex items-center gap-2 text-slate-600 hover:text-[#0B192C]" data-testid="footer-email"><Mail className="h-3.5 w-3.5" />{C.email}</a></li>
              <li><a href={`https://wa.me/${C.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-600 hover:text-[#0B192C]" data-testid="footer-whatsapp"><MessageCircle className="h-3.5 w-3.5" />{t.footer.whatsapp}</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12 pt-6 border-t border-slate-200">
          <p className="text-xs text-slate-400 font-display">© {new Date().getFullYear()} {C.legalName}. {t.footer.rights}</p>
        </div>
      </footer>
    </div>
  );
}
